import { AutoAwesome, ContentPaste, Lightbulb, Psychology } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Paper,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useState } from 'react';

interface MvpOpportunityAiStepProps {
    onAnalyze: (text: string) => void;
    loading?: boolean;
    error?: string;
    aiConfirmation: boolean;
    setAiConfirmation: (val: boolean) => void;
    setText: (text: string) => void;
}

/**
 * AI analysis step for opportunity creation
 */
function MvpOpportunityAiStep({
    onAnalyze,
    loading = false,
    error,
    aiConfirmation,
    setAiConfirmation,
    setText,
}: MvpOpportunityAiStepProps) {
    const theme = useTheme();
    const [text, setLocalText] = useState('');

    const handlePaste = async () => {
        try {
            const clipboardText = await navigator.clipboard.readText();
            setLocalText(clipboardText);
            setText(clipboardText);
        } catch (err) {
            console.error('Fehler beim Einfügen:', err);
        }
    };

    const handleAnalyze = () => {
        if (text.trim() && aiConfirmation) {
            onAnalyze(text);
        }
    };

    return (
        <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <AutoAwesome
                    sx={{
                        fontSize: 64,
                        color: 'primary.main',
                        mb: 2,
                        filter: `drop-shadow(0 4px 8px ${alpha(theme.palette.primary.main, 0.3)})`,
                    }}
                />
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                    T4lo-Analyse
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Füge den Text der Projektanfrage ein und lass ihn von T4lo analysieren
                </Typography>
            </Box>

            {/* Input Section */}
            <Paper
                sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Anfrage-Text
                    </Typography>
                    <Button
                        startIcon={<ContentPaste />}
                        onClick={handlePaste}
                        variant="outlined"
                        size="small"
                        sx={{ borderRadius: 2 }}
                    >
                        Einfügen
                    </Button>
                </Box>

                <TextField
                    fullWidth
                    multiline
                    rows={8}
                    value={text}
                    onChange={(e) => {
                        setLocalText(e.target.value);
                        setText(e.target.value);
                    }}
                    placeholder="Füge hier den Text der Projektanfrage ein. T4lo wird automatisch relevante Informationen wie Projektdauer, Anforderungen, Standort und weitere Details extrahieren..."
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& fieldset': {
                                borderColor: alpha(theme.palette.primary.main, 0.2),
                            },
                            '&:hover fieldset': {
                                borderColor: theme.palette.primary.main,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main,
                            },
                        },
                    }}
                />
            </Paper>

            {/* AI Features Info */}
            <Paper
                sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 3,
                    background: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Psychology color="info" />
                    Was T4lo für dich analysiert
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
                    {[
                        'Projekttitel und Beschreibung',
                        'Zeitrahmen und Deadlines',
                        'Benötigte Skills und Kompetenzen',
                        'Standort und Remote-Anteil',
                        'Kunde und Partner',
                        'Tagessätze und Konditionen',
                        'Anzahl benötigter Mitarbeiter',
                        'Zertifikate und Qualifikationen',
                    ].map((feature, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Lightbulb sx={{ fontSize: 16, color: 'info.main' }} />
                            <Typography variant="body2">{feature}</Typography>
                        </Box>
                    ))}
                </Box>
            </Paper>

            {/* Confirmation */}
            <Box sx={{ mb: 3 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={aiConfirmation}
                            onChange={(e) => setAiConfirmation(e.target.checked)}
                            color="primary"
                        />
                    }
                    label={
                        <Typography variant="body2">
                            Ich bestätige, dass der eingefügte Text zur Analyse an T4lo gesendet werden darf. Sensible
                            Daten werden nicht gespeichert.
                        </Typography>
                    }
                />
            </Box>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Loading State */}
            {loading && (
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                        T4lo analysiert deinen Text. Dies kann einen Moment dauern...
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

export default MvpOpportunityAiStep;
